import React, { createContext, useContext } from 'react';

/**
 * React Context used by `next-data-hooks` to pull static data.
 */
const NextDataHooksContext = /*#__PURE__*/createContext(null);

const stub = () => {
  throw new Error('Create data hook was run in the browser. See https://github.com/ricokahler/next-data-hooks#code-elimination');
};

/**
 * Creates a data hook.
 *
 * @param key The key to uniquely identify this data hooks from other on the same page.
 * @param getData An async for data that will be called via `getStaticProps` in next.js
 * @return A hook that can be used in any component within the page's React tree
 */
function createDataHook(key, getData) {
  function useData() {
    const dataHooksContext = useContext(NextDataHooksContext);
    if (!dataHooksContext) {
      throw new Error('Could not find `NextDataHooksContext`. Ensure `NextDataHooksProvider` is configured correctly.');
    }
    const dataHooksValue = dataHooksContext[key];
    if (!Object.keys(dataHooksContext).includes(key)) {
      throw new Error(`Did not find a data hook named "${key}". Ensure it was provided to getDataHooksProps.`);
    }
    return dataHooksValue;
  }
  return Object.assign(useData, {
    // After running it through the babel plugin, the `getData` arg will be
    // undefined in the browser
    getData: getData || stub,
    key
  });
}

/**
 * Pulls the data from the next-data-hooks and returns the props to be received
 * by the NextDataHooksProvider
 */
async function getDataHooksProps({
  dataHooks,
  context
}) {
  const hookKeys = {};

  // we allow the same function reference to be added to the array more than
  // once so we de-dupe here
  const deDupedHooks = Array.from(new Set(dataHooks));
  for (const hook of deDupedHooks) {
    if (hookKeys[hook.key]) {
      throw new Error(`Found duplicate hook key "${hook.key}". Ensure all hook keys per \`createDatHooksProps\` call are unique.`);
    }
    hookKeys[hook.key] = true;
  }
  const entries = await Promise.all(dataHooks.map(async hook => {
    const data = await hook.getData(context);
    return [hook.key, data];
  }));
  return {
    nextDataHooks: Object.fromEntries(entries)
  };
}

/**
 * Injects the data from data hooks into React Context. Place this in `_app`
 */
function NextDataHooksProvider({
  nextDataHooks,
  children
}) {
  return /*#__PURE__*/React.createElement(NextDataHooksContext.Provider, {
    value: nextDataHooks
  }, children);
}

function isServerSidePropsContext(context) {
  if (typeof context !== 'object' || !context) return false;
  return !!context.req && !!context.res;
}

export { NextDataHooksContext, NextDataHooksProvider, createDataHook, getDataHooksProps, isServerSidePropsContext };
//# sourceMappingURL=index.esm.js.map
